import { useEffect, useState } from "react";
import { Loader2, Settings as SettingsIcon } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const TYPE_LABELS = {
  enhanced_dbs: "Enhanced DBS",
  safeguarding_training: "Safeguarding Training",
  first_aid: "First Aid",
  send_training: "SEND Training",
  itt_competency: "Independent Travel Training Competency",
  other: "Other organisational training",
};

const DEFAULT_LEVELS = [
  { level: 1, name: "Trainer demonstration", description: "Trainer leads and demonstrates the complete journey." },
  { level: 2, name: "Full support", description: "Child participates while trainer provides substantial support." },
  { level: 3, name: "Moderate support", description: "Child leads parts of the journey with regular trainer support." },
  { level: 4, name: "Prompting", description: "Child leads the journey with verbal/visual prompts where required." },
  { level: 5, name: "Minimal prompting", description: "Child completes most of the journey with very limited intervention." },
  { level: 6, name: "Shadowing", description: "Child leads while trainer observes from an appropriate distance per organisational procedure and the individual risk assessment." },
  { level: 7, name: "Independent journey assessment", description: "Formal assessment of whether the child can complete the agreed journey safely and independently." },
];

export default function Settings() {
  const [settings, setSettings] = useState(null);
  const [alertDays, setAlertDays] = useState("30");
  const [required, setRequired] = useState([]);
  const [riskLowMax, setRiskLowMax] = useState("6");
  const [riskMediumMax, setRiskMediumMax] = useState("12");
  const [levels, setLevels] = useState([]);
  const [pauseReasons, setPauseReasons] = useState("");
  const [incidentCategories, setIncidentCategories] = useState("");
  const [sgCategories, setSgCategories] = useState("");
  const [saving, setSaving] = useState(false);
  const [appMode, setAppMode] = useState("live");
  const [signinMorning, setSigninMorning] = useState("");
  const [signinAfternoon, setSigninAfternoon] = useState("");
  const { user } = useAuth();

  const saveMode = async (mode) => {
    try {
      await api.patch("/settings", { app_mode: mode });
      setAppMode(mode);
      toast.success(mode === "demo" ? "Demo/training mode ON — banner now shows on every screen" : "Live mode — demo banner removed");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => {
    api.get("/settings").then((r) => {
      setSettings(r.data);
      setAlertDays(String(r.data.compliance_alert_days ?? 30));
      setRequired(r.data.required_compliance_types || []);
      setRiskLowMax(String(r.data.risk_low_max ?? 6));
      setRiskMediumMax(String(r.data.risk_medium_max ?? 12));
      setLevels(r.data.independence_levels || []);
      setPauseReasons((r.data.pause_reasons || []).join("\n"));
      setIncidentCategories((r.data.incident_categories || []).join("\n"));
      setSgCategories((r.data.safeguarding_categories || []).join("\n"));
      setAppMode(r.data.app_mode || "live");
      setSigninMorning(r.data.signin_alert_morning || "");
      setSigninAfternoon(r.data.signin_alert_afternoon || "");
    }).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const toggleRequired = (t) => {
    setRequired((cur) => (cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t]));
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.patch("/settings", {
        compliance_alert_days: parseInt(alertDays, 10),
        required_compliance_types: required,
        risk_low_max: parseInt(riskLowMax, 10),
        risk_medium_max: parseInt(riskMediumMax, 10),
      });
      setSettings(data);
      toast.success("Settings saved");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!settings) {
    return (
      <div className="flex items-center gap-2 text-slate-600" data-testid="settings-loading">
        <Loader2 className="h-5 w-5 animate-spin" /> Loading settings…
      </div>
    );
  }

  return (
    <div data-testid="settings-page" className="space-y-6 max-w-3xl">
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Settings</h1>
        <p className="text-sm text-slate-600 mt-1">Organisation configuration. Restricted to authorised administrators.</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <SettingsIcon className="h-5 w-5 text-primary" /> Compliance alerts
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={save} className="space-y-5" data-testid="settings-form">
            <div className="space-y-1.5 max-w-xs">
              <Label>Default alert window (days before expiry)</Label>
              <Input data-testid="settings-alert-days-input" type="number" min="1" max="365" value={alertDays}
                onChange={(e) => setAlertDays(e.target.value)} />
              <p className="text-xs text-slate-500">
                Compliance records move to “Expiring soon” this many days before their renewal date. Individual
                records can override this. Requirements without a fixed expiry are never auto-expired.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Required for all staff</Label>
              <p className="text-xs text-slate-500 mb-1">Staff missing any of these appear as compliance alerts.</p>
              {Object.entries(TYPE_LABELS).map(([key, label]) => (
                <label key={key} className="flex items-center gap-2 text-sm text-slate-700 min-h-[44px]">
                  <Checkbox data-testid={`settings-required-${key}`} checked={required.includes(key)} onCheckedChange={() => toggleRequired(key)} />
                  {label}
                </label>
              ))}
            </div>

            <Button data-testid="settings-save-button" type="submit" disabled={saving}
              className="h-11 bg-primary hover:bg-brand-hover text-white font-semibold">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save settings"}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <SettingsIcon className="h-5 w-5 text-primary" /> Journey risk matrix
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={save} className="space-y-5" data-testid="settings-risk-matrix-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-lg">
              <div className="space-y-1.5">
                <Label>LOW band maximum score</Label>
                <Input data-testid="settings-risk-low-max" type="number" min="1" max="24" value={riskLowMax}
                  onChange={(e) => setRiskLowMax(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>MEDIUM band maximum score</Label>
                <Input data-testid="settings-risk-medium-max" type="number" min="2" max="25" value={riskMediumMax}
                  onChange={(e) => setRiskMediumMax(e.target.value)} />
              </div>
            </div>
            <p className="text-xs text-slate-500">
              Risk scores are Likelihood (1–5) × Severity (1–5). Scores up to the LOW maximum are LOW; up to the
              MEDIUM maximum are MEDIUM; anything above is HIGH. The LOW maximum must be lower than the MEDIUM
              maximum. Bands are decision-support only — they never declare a journey safe or suitable.
            </p>
            <Button data-testid="settings-risk-save-button" type="submit" disabled={saving}
              className="h-11 bg-primary hover:bg-brand-hover text-white font-semibold">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save settings"}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <SettingsIcon className="h-5 w-5 text-primary" /> Training independence levels
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3" data-testid="settings-levels-form">
            <p className="text-xs text-slate-500">
              Configure the progressive independence levels used in training programmes. Levels are professional
              judgements recorded by authorised staff — progression is never automatic.
            </p>
            {levels.map((l, i) => (
              <div key={l.level} className="grid grid-cols-1 sm:grid-cols-[64px_1fr_2fr] gap-2 items-center" data-testid={`settings-level-${l.level}`}>
                <span className="text-sm font-semibold text-slate-700">L{l.level}</span>
                <Input value={l.name} data-testid={`settings-level-name-${l.level}`}
                  onChange={(e) => setLevels((cur) => cur.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} />
                <Input value={l.description} placeholder="Description" data-testid={`settings-level-desc-${l.level}`}
                  onChange={(e) => setLevels((cur) => cur.map((x, j) => (j === i ? { ...x, description: e.target.value } : x)))} />
              </div>
            ))}
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" data-testid="settings-level-add" className="h-11"
                onClick={() => setLevels((cur) => [...cur, { level: cur.length + 1, name: "", description: "" }])}>Add level</Button>
              <Button type="button" variant="outline" data-testid="settings-levels-restore-defaults" className="h-11"
                onClick={() => { setLevels(DEFAULT_LEVELS.map((l) => ({ ...l }))); toast.success("Recommended wording loaded — review and press Save levels"); }}>
                Restore recommended wording
              </Button>
              {levels.length > 1 && (
                <Button type="button" variant="outline" data-testid="settings-level-remove" className="h-11"
                  onClick={() => setLevels((cur) => cur.slice(0, -1))}>Remove last</Button>
              )}
              <Button data-testid="settings-levels-save" className="h-11 bg-primary text-white font-semibold" disabled={saving}
                onClick={async () => {
                  setSaving(true);
                  try {
                    const { data } = await api.patch("/settings", { independence_levels: levels });
                    setSettings(data);
                    toast.success("Independence levels saved");
                  } catch (e) {
                    toast.error(formatApiError(e));
                  } finally {
                    setSaving(false);
                  }
                }}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save levels"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <SettingsIcon className="h-5 w-5 text-primary" /> Incident &amp; safeguarding categories
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="settings-categories-form">
            <div className="space-y-2">
              <Label>Incident categories (one per line, snake_case)</Label>
              <Textarea rows={8} value={incidentCategories} data-testid="settings-incident-categories"
                onChange={(e) => setIncidentCategories(e.target.value)} />
              <Button data-testid="settings-incident-categories-save" className="h-11 bg-primary text-white font-semibold" disabled={saving}
                onClick={async () => {
                  setSaving(true);
                  try {
                    const { data } = await api.patch("/settings", { incident_categories: incidentCategories.split("\n") });
                    setSettings(data);
                    toast.success("Incident categories saved");
                  } catch (e) {
                    toast.error(formatApiError(e));
                  } finally {
                    setSaving(false);
                  }
                }}>
                Save incident categories
              </Button>
            </div>
            <div className="space-y-2">
              <Label>Safeguarding categories (one per line, snake_case)</Label>
              <Textarea rows={8} value={sgCategories} data-testid="settings-sg-categories"
                onChange={(e) => setSgCategories(e.target.value)} />
              <Button data-testid="settings-sg-categories-save" className="h-11 bg-primary text-white font-semibold" disabled={saving}
                onClick={async () => {
                  setSaving(true);
                  try {
                    const { data } = await api.patch("/settings", { safeguarding_categories: sgCategories.split("\n") });
                    setSettings(data);
                    toast.success("Safeguarding categories saved");
                  } catch (e) {
                    toast.error(formatApiError(e));
                  } finally {
                    setSaving(false);
                  }
                }}>
                Save safeguarding categories
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <SettingsIcon className="h-5 w-5 text-primary" /> Programme pause reasons
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 max-w-lg" data-testid="settings-pause-reasons-form">
            <Label>Pause reasons (one per line, snake_case or plain text)</Label>
            <Textarea rows={5} value={pauseReasons} data-testid="settings-pause-reasons"
              onChange={(e) => setPauseReasons(e.target.value)} />
            <Button data-testid="settings-pause-reasons-save" className="h-11 bg-primary text-white font-semibold" disabled={saving}
              onClick={async () => {
                setSaving(true);
                try {
                  const { data } = await api.patch("/settings", { pause_reasons: pauseReasons.split("\n") });
                  setSettings(data);
                  toast.success("Pause reasons saved");
                } catch (e) {
                  toast.error(formatApiError(e));
                } finally {
                  setSaving(false);
                }
              }}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save reasons"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card data-testid="signin-alerts-card">
        <CardHeader>
          <CardTitle className="text-lg">Staff sign-in alerts</CardTitle>
          <p className="text-xs text-slate-500">Set morning and/or afternoon deadlines (UK time). If any trainer has not started their shift by the deadline, the Co-ordinator and Manager get an alert naming them. Leave blank to disable.</p>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-3 max-w-lg">
            <div className="space-y-1.5">
              <Label>Morning deadline</Label>
              <Input type="time" data-testid="signin-alert-morning" value={signinMorning} onChange={(e) => setSigninMorning(e.target.value)} className="w-36" />
            </div>
            <div className="space-y-1.5">
              <Label>Afternoon deadline</Label>
              <Input type="time" data-testid="signin-alert-afternoon" value={signinAfternoon} onChange={(e) => setSigninAfternoon(e.target.value)} className="w-36" />
            </div>
            <Button data-testid="signin-alerts-save" className="h-11 bg-primary text-white font-semibold" disabled={saving}
              onClick={async () => {
                setSaving(true);
                try {
                  await api.patch("/settings", { signin_alert_morning: signinMorning, signin_alert_afternoon: signinAfternoon });
                  toast.success("Sign-in alert times saved");
                } catch (e) {
                  toast.error(formatApiError(e));
                } finally {
                  setSaving(false);
                }
              }}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save alert times"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {user?.role === "super_admin" && (
        <Card data-testid="app-mode-card">
          <CardHeader>
            <CardTitle className="text-lg">App mode</CardTitle>
            <p className="text-xs text-slate-500">Demo/training mode shows a highly visible banner on every screen so staff training sessions and screenshots are clearly marked as not real data. Switch back to Live for real casework.</p>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {[["live", "Live mode"], ["demo", "Demo / training mode"]].map(([m, label]) => (
                <button key={m} type="button" data-testid={`app-mode-${m}`} onClick={() => saveMode(m)}
                  className={`h-11 px-5 rounded-lg border text-sm font-semibold transition-colors duration-200 ${appMode === m ? (m === "demo" ? "bg-amber-500 text-white border-amber-500" : "bg-primary text-white border-primary") : "bg-white text-slate-700 border-border hover:bg-muted"}`}>
                  {label}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {user?.role === "super_admin" && <DemoDataPurge />}
    </div>
  );
}

function DemoDataPurge() {
  const [preview, setPreview] = useState(null);
  const [selected, setSelected] = useState([]);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/settings/demo-data/preview").then((r) => setPreview(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  if (!preview) return null;
  const canPurge = confirm === "DELETE DEMO DATA" && !busy &&
    (preview.demo_accounts.length > 0 || selected.length > 0 || preview.test_councils.length > 0 || preview.test_schools.length > 0);

  const purge = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/settings/demo-data/purge", {
        confirm, child_ids: selected, include_accounts: true, include_test_sites: true,
      });
      toast.success(`Demo data deleted — ${data.deleted.users || 0} accounts, ${selected.length} child case(s), ${(data.deleted.councils || 0) + (data.deleted.schools || 0)} test councils/schools`);
      if (data.skipped_sites?.length) toast.info(`Kept (in use by a real case): ${data.skipped_sites.join(", ")}`);
      setConfirm("");
      setSelected([]);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="border-red-200" data-testid="demo-purge-card">
      <CardHeader>
        <CardTitle className="text-lg text-red-700">Danger zone — demo &amp; test data</CardTitle>
        <p className="text-xs text-slate-500">Permanently delete demo login accounts, selected test child cases (with all their records), and TEST councils/schools. Manager (super admin) only. This cannot be undone. Audit logs are retained for the compliance trail.</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div data-testid="demo-purge-accounts">
          <Label>Demo login accounts to delete ({preview.demo_accounts.length})</Label>
          <p className="text-xs text-slate-600 mt-1 font-mono break-all">
            {preview.demo_accounts.map((a) => a.email).join(", ") || "None"}
          </p>
        </div>
        <div data-testid="demo-purge-cases">
          <Label>Test child cases to delete (tick to include — all their assessments, plans, programmes, sessions, incidents and feedback are deleted too)</Label>
          <div className="mt-1.5 space-y-1.5">
            {preview.children.map((c) => (
              <label key={c.id} className="flex items-center gap-2 text-sm" data-testid={`purge-case-${c.case_id}`}>
                <Checkbox checked={selected.includes(c.id)}
                  onCheckedChange={() => setSelected((cur) => (cur.includes(c.id) ? cur.filter((x) => x !== c.id) : [...cur, c.id]))} />
                <span className="font-medium">{c.case_id}</span> — {c.name} <span className="text-xs text-slate-500">({(c.status || "").replace(/_/g, " ")})</span>
              </label>
            ))}
          </div>
        </div>
        <p className="text-xs text-slate-600" data-testid="demo-purge-sites">
          Test councils/schools to delete: {preview.test_councils.length + preview.test_schools.length} (any still linked to a case you keep will be skipped automatically)
        </p>
        <div className="space-y-1.5 max-w-md">
          <Label>Type DELETE DEMO DATA to confirm</Label>
          <Input data-testid="demo-purge-confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="DELETE DEMO DATA" />
        </div>
        <Button data-testid="demo-purge-button" disabled={!canPurge} onClick={purge}
          className="h-11 bg-red-600 hover:bg-red-700 text-white font-semibold">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Permanently delete demo data"}
        </Button>
      </CardContent>
    </Card>
  );
}
